import { computed, watch, ref } from 'vue';
import { useConvoStore } from 'src/stores/conversations';
import { useLoggingStore } from 'src/stores/logging';
import { of, merge, zip } from 'rxjs';
import { catchError, filter, map, retry, switchMap, tap, } from 'rxjs/operators';
import { rxInitSubjectCallback, useObservable } from 'src/plugins/rxjs/base';
import { v4 as uuidv4 } from 'uuid';
import { ChatMiscDataPlugin } from 'src/features/messaging/chat/components/methods/misc';
import { StandardAlertsPlugin } from 'src/plugins/alert/alertPlugin';
/**
 * Plugin containing misc methods for Chat component
 */
const ChatUploadPlugin = (uploadErrorCallback, pushedToQueueCallback, uploadedCb) => {
    const convoStore = useConvoStore();
    const loggingStore = useLoggingStore();
    const { user, convo, receiver } = ChatMiscDataPlugin();
    const { errorAlert } = StandardAlertsPlugin();
    const uploading = computed({
        get: () => {
            return convoStore.uploading;
        },
        set: (newValue) => {
            void convoStore.updateUploading(newValue);
        }
    });
    // mimic msgs for display with some elements for reupload
    const errorStore = ref([]);
    // upload data
    const uploadQueue = ref([]);
    const { subject: upload$ } = rxInitSubjectCallback();
    const { subject: reupload$ } = rxInitSubjectCallback();
    useObservable(merge(upload$.pipe(map(({ text, fileUpload, previewDimensions, uploadType }) => {
        if (fileUpload.size > 20000000) {
            errorAlert('File size is too large');
            uploadErrorCallback();
            return null;
        }
        const fullname = fileUpload.name.split('.');
        const ending = fullname.pop();
        const randomId = Math.random().toString(36).substr(2, 5);
        let filename = fullname[0];
        if (!filename) {
            filename = uuidv4();
        }
        return {
            dimensions: { ...previewDimensions },
            ending: ending,
            file: fileUpload,
            filename: filename,
            metadata: {
                contentType: fileUpload.type,
                customMetadata: {
                    authUser1: receiver.value,
                    authUser2: user.value.user_pk.toString()
                }
            },
            path: `/images/${user.value.user_pk}/${convo.value.id}/${filename}_${randomId}.${ending}`,
            text: text,
            type: uploadType || 'image',
            url: [URL.createObjectURL(fileUpload)]
        };
    }), filter(v => v !== null)), reupload$.pipe(map((index) => {
        const errStore = [...errorStore.value];
        const errorData = errStore.splice(index, 1)[0];
        errorStore.value = errStore;
        return errorData;
    }))).pipe(map((v) => v), tap((v) => {
        uploadQueue.value = [
            ...uploadQueue.value, {
                ...v,
                uploadUUid: uuidv4()
            }
        ];
        pushedToQueueCallback();
    })));
    /**
     * Uploading
     */
    const uploadProgress = ref({});
    const { subject: uploadQueueWatcher$ } = rxInitSubjectCallback();
    useObservable(uploadQueueWatcher$.pipe(filter(() => !uploading.value), map(({ newValue }) => newValue), filter(v => v.length > 0), map(v => v[0]), switchMap(v => {
        uploading.value = true;
        return of('').pipe(switchMap(() => {
            return import(
            /* webpackChunkName: "firebaseMethod" */
            'src/_helpers/firebaseMethods');
        }), map(({ default: firebaseMethods }) => firebaseMethods), switchMap(firebaseMethods => {
            if (process.env.SERVER) {
                console.log("hit 12");
            }
            return zip(firebaseMethods(), import(
            /* webpackChunkName: "firebaseMethod" */
            'firebase/storage'), import(
            /* webpackChunkName: "firebaseRx" */
            'rxfire/storage'));
        }), switchMap(([{ storage }, { ref: fbStorageRef }, { uploadBytesResumable, getDownloadURL }]) => {
            const fileRef = fbStorageRef(storage, v.path);
            return uploadBytesResumable(fileRef, v.file, v.metadata).pipe(catchError(err => {
                loggingStore.errorHandler(err);
                errorStore.value = [
                    ...errorStore.value,
                    v
                ];
                return of('');
            }), filter((v) => v !== ''), map((snap) => snap), map((snap) => {
                /**
                 * Upload to firestore
                 */
                const progress = (snap.bytesTransferred / snap.totalBytes);
                uploadProgress.value = {
                    ...uploadProgress.value,
                    [v.uploadUUid]: progress
                };
                return progress * 100;
            }), filter(progress => progress === 100), switchMap(() => {
                return of('').pipe(switchMap(() => {
                    return getDownloadURL(fileRef).pipe(tap((downloadURL) => {
                        /**
                         * Get url and send to firestore
                         */
                        // get data from queue
                        const dataStore = [...uploadQueue.value];
                        const uploadIndex = dataStore.findIndex((element) => {
                            return element.uploadUUid === v.uploadUUid;
                        });
                        const pulledData = dataStore.splice(uploadIndex, 1)[0];
                        dataStore.splice(uploadIndex, 1);
                        // remove progress
                        const uploadProgressCopy = { ...uploadProgress.value };
                        delete uploadProgressCopy[pulledData.uploadUUid];
                        uploadProgress.value = uploadProgressCopy;
                        // send to parent component
                        uploadedCb({
                            ...pulledData,
                            url: [downloadURL]
                        });
                        uploading.value = false;
                        uploadQueue.value = dataStore;
                    }));
                }), retry({
                    delay: 500
                }));
            }));
        }));
    })));
    watch(uploadQueue, (newValue, oldValue) => {
        uploadQueueWatcher$.next({ newValue, oldValue });
    });
    return {
        uploadProgress,
        errorStore,
        uploadQueue,
        upload$,
        reupload$
    };
};
export { ChatUploadPlugin };
